import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from "@angular/core";
import {Condition} from "../../model/condition";
import {Subject, Subscription} from "rxjs";
import {Factor} from "../../model/factor";
import {SliderService} from "../../Services/slider-service";
import {ConditionFactorService} from "../../Services/condition-factor-service";
import {DragulaService} from "ng2-dragula";
import {takeUntil} from 'rxjs/operators';

@Component({
  selector: 'gen-condition-editor',
  templateUrl: './condition-editor.component.html',
  styleUrls: ['./condition-editor.component.scss', '../../trial-curation-styles.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConditionEditorComponent implements OnInit, OnDestroy {

  @Input() condition: Condition;
  @Input() first: boolean;
  @Input() type: string;
  @Output() cloneMe = new EventEmitter<Condition>();
  @Output() deleteMe = new EventEmitter<Condition>();

  private conditionFactorSubscription: Subscription = null;
  private destroyer$: Subject<null> = new Subject<null>();

  // initialize the base number (as current time milliseconds) for generating unique object's ID
  private static baseIdCounter = performance.now();
  public listName: string = undefined;

  constructor(private dragulaService: DragulaService, private changeDetector: ChangeDetectorRef,
              private conditionFactorService: ConditionFactorService, private sliderService: SliderService) {
  }

  ngOnInit() {
    // set a unique list's name to ensure no duplications in dragulaService's bag list
    if (!this.listName) {
      this.listName = 'factor_items_' + ConditionEditorComponent.baseIdCounter; //don't increment we want all condition bags to have same id so factors can move among them
    }

    // to use the dragulaService, it is required to call the setOptions method, specifying the container and providing
    // an object literal with your desired options
    if (! this.dragulaService.find(this.listName)) { // need to search first that name is not already added to avoid an error
      this.dragulaService.setOptions(this.listName, {
        revertOnSpill: true,  // spilling will put the element back where it was dragged from, if this is true
        removeOnSpill: false, // spilling will `.remove` the element, if this is true
        moves: (element, source, handle, sibling) => {
          return handle.className.startsWith('fa fa-bars');
        },
        accepts: function(el, target, source, sibling) {
          // el - the item that is being dropped
          // target - the container on which the item is being dropped
          // source - the container from which the item was dragged
          // sibling - the item in the target container before which the item is being dropped, null if being dropped as last item
          return sibling != null;  //we need this to prevent moving factor to the other side of Add Query button
        }
      });
    }

    this.dragulaService.drop
      .pipe(
        takeUntil(this.destroyer$.asObservable())
      )
      .subscribe(data => {
        const element = data[0];
        const sibling = data[1];
        const target = data[2];
        const source = data[3];

        if (element === this.listName) {
          this.changeDetector.markForCheck();
        }
      });

    this.conditionFactorSubscription = this.conditionFactorService.cancel$
      .subscribe((cancel: boolean) => {
        if (cancel) {
          let size: number = this.condition.factors.length;
          if (size > 0) {
            let factor: Factor = this.condition.factors[size-1];
            if (!factor.queryNode) {
              this.condition.factors.pop();
            }
          }
        }
      });
  }

  ngOnDestroy() {
    if (this.conditionFactorSubscription !== null)
      this.conditionFactorSubscription.unsubscribe();

    this.destroyer$.next();
    this.destroyer$.complete();
    this.destroyer$.unsubscribe();

    // remove the current list from the dragulaService's bag list
    if (this.dragulaService.find(this.listName)) { // need to search first that name exists to avoid an error
      this.dragulaService.destroy(this.listName);
    }
  }

  public get operator() {
    switch (this.type) {
      case "any":
        return "OR";
      case "all":
        return "AND";
      case "none":
        return "OR";
      default:
        return "ERROR";
    }
  }

  addFactor(): void {
    let newFactor = new Factor();
    this.sliderService.editFactor(newFactor);
    this.condition.factors.push(newFactor);
  }

  duplicateFactor(factor: Factor): void {
    let k = this.condition.factors.indexOf(factor);
    let newFactor = JSON.parse(JSON.stringify(factor));
    this.condition.factors.splice(k, 0, newFactor);
  }

  deleteFactor(factor: Factor): void {
    if (!this.condition.factors) this.condition.factors = [];
    this.deleteThisFacor(factor);
  }

  private deleteThisFacor(factor: Factor) {
    this.condition.factors = this.condition.factors.filter(x => x != factor);
  }

  onCloneMe(): void {
    this.cloneMe.emit(this.condition);
  }

  onDeleteMe(): void {
    this.deleteMe.emit(this.condition);
  }

}
